// MF-67 — Journal Zones list (Maestro-managed school areas). Standard row actions
// (preview / edit / delete-with-confirmation), sorted by name.
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { configPath } from '../paths'
import { useNavigate } from 'react-router-dom'
import { Button, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import RowActions from '../components/RowActions'
import ConfirmDelete from '../components/ConfirmDelete'
import PreviewDialog, { PreviewSection } from '../components/PreviewDialog'
import ListSearch from '../components/ListSearch'
import ListTable, { Muted } from '../components/ListTable'
import MapPicker from '../components/MapPicker'
import { loadZones } from '../../../../store/actions/journalZonesActions'
import { deleteZone } from '../../../../services/journal/zones.service'

const hasPin = (z) => z?.lat != null && z?.lng != null
const fmtCoord = (z) => `${Number(z.lat).toFixed(5)}, ${Number(z.lng).toFixed(5)}`

const ZonesList = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { list, loading, totalRecords } = useSelector((state) => state.journalZones.zones)
  const [toDelete, setToDelete] = useState(null)
  const [preview, setPreview] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [search, setSearch] = useState('')

  const reload = () =>
    dispatch(loadZones({ page: page + 1, limit: rowsPerPage, ...(search ? { search } : {}) }))

  useEffect(() => {
    dispatch(loadZones({ page: page + 1, limit: rowsPerPage, ...(search ? { search } : {}) }))
  }, [dispatch, page, rowsPerPage, search])

  const onConfirmDelete = async () => {
    setDeleting(true)
    try {
      await deleteZone(toDelete._id)
      setToDelete(null)
      reload()
    } finally {
      setDeleting(false)
    }
  }

  const columns = [
    { key: 'name', label: 'Zone', render: (r) => <span style={{ fontWeight: 500 }}>{r.name}</span> },
    {
      key: 'pin',
      label: 'Map pin',
      width: 220,
      render: (r) =>
        hasPin(r) ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            <FontAwesomeIcon icon={['fal', 'map-marker-alt']} style={{ color: 'rgb(37, 99, 235)' }} />
            {fmtCoord(r)}
          </span>
        ) : (
          <Muted>No pin</Muted>
        )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 196,
      align: 'right',
      render: (r) => (
        <RowActions
          onPreview={() => setPreview(r)}
          onEdit={() => navigate(configPath('zones', `edit/${r._id}`))}
          onDelete={() => setToDelete(r)}
        />
      )
    }
  ]

  return (
    <>
      <div style={{ padding: '8px 8px 24px' }}>
        <ListSearch
          value={search}
          placeholder='Search zones…'
          onChange={(v) => {
            setSearch(v)
            setPage(0)
          }}
        >
          {!loading ? (
            <Typography style={{ fontSize: 13, color: 'rgb(100, 116, 139)' }}>
              {totalRecords} {totalRecords === 1 ? 'zone' : 'zones'}
            </Typography>
          ) : null}
          <Button
            variant='contained'
            onClick={() => navigate(configPath('zones', 'add'))}
            startIcon={<FontAwesomeIcon icon={['fas', 'plus']} style={{ fontSize: 11 }} />}
          >
            New zone
          </Button>
        </ListSearch>
        <ListTable
          columns={columns}
          rows={list}
          loading={loading}
          emptyText={
            search
              ? 'No zones match your search'
              : 'No zones yet — add the areas of your school (Yard, Gym, Bus bay…)'
          }
          pagination={{
            count: totalRecords,
            page,
            rowsPerPage,
            onPageChange: setPage,
            onRowsPerPageChange: (n) => {
              setRowsPerPage(n)
              setPage(0)
            }
          }}
        />
      </div>

      <ConfirmDelete
        open={Boolean(toDelete)}
        entity='zone'
        name={toDelete?.name}
        loading={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={onConfirmDelete}
      />
      <PreviewDialog
        open={Boolean(preview)}
        title={preview?.name || 'Zone'}
        onClose={() => setPreview(null)}
        rows={[{ label: 'Map pin', value: hasPin(preview) ? fmtCoord(preview) : null }]}
      >
        {hasPin(preview) ? (
          <PreviewSection title='On the map'>
            <MapPicker
              value={{ lat: preview.lat, lng: preview.lng }}
              center={preview}
              onChange={() => {}}
              height={220}
            />
          </PreviewSection>
        ) : null}
      </PreviewDialog>
    </>
  )
}

export default ZonesList
